import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const EMPTY = { name_ar: "", name_en: "", rating: 5, text_ar: "", text_en: "", active: true, order: 0 };

export default function ReviewsManage() {
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/admin/reviews").then((r) => setReviews(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/reviews/${data.id}`, data);
      else await api.post("/admin/reviews", data);
      toast.success("تم الحفظ"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("حذف التقييم؟")) return; await api.delete(`/admin/reviews/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">إدارة التقييمات</h1>
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full" data-testid="add-review-btn"><Plus className="h-4 w-4 me-1" /> تقييم جديد</Button>
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-10" /> : (
        <div className="grid gap-3 sm:grid-cols-2">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-2xl bg-card cb-card-shadow p-4" data-testid={`admin-review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><span className="font-semibold">{r.name_ar}</span>{!r.active && <span className="text-xs text-rose-500">مخفي</span>}</div>
                <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-primary text-primary" : "text-muted"}`} />)}</div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{r.text_ar}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setEditing(r)} className="rounded-full border-2"><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ReviewDialog editing={editing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function ReviewDialog({ editing, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!editing) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader><DialogTitle>{form.id ? "تعديل تقييم" : "تقييم جديد"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1 block text-xs">اسم العميلة</Label><Input value={form.name_ar} onChange={(e) => set("name_ar", e.target.value)} className="rounded-xl h-11" data-testid="review-name" /></div>
          <div><Label className="mb-1 block text-xs">التقييم (1-5)</Label><Input type="number" min={1} max={5} value={form.rating} onChange={(e) => set("rating", +e.target.value)} className="rounded-xl h-11" data-testid="review-rating" /></div>
          <div><Label className="mb-1 block text-xs">النص (عربي)</Label><Textarea value={form.text_ar} onChange={(e) => set("text_ar", e.target.value)} className="rounded-xl" rows={3} data-testid="review-text" /></div>
          <div><Label className="mb-1 block text-xs">النص (إنجليزي)</Label><Textarea value={form.text_en} onChange={(e) => set("text_en", e.target.value)} className="rounded-xl" rows={2} dir="ltr" /></div>
          <label className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} /> <span className="text-sm">ظاهر في الموقع</span></label>
          <Button onClick={() => onSave(form)} className="rounded-full w-full h-11 font-semibold" data-testid="review-save-btn">حفظ</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
